/**
 * SCRUM-130/131/132/134 — Digital Signage player fullscreen.
 * /signage/:token — pantalla dedicada del comercio (TV, tablet, mini-PC).
 *
 * NO es el Customer Display (/display/:token, migración 216).
 *
 * - Poll cada 5s del contenido → si cambia, resetea la rotación de playlist.
 * - Wake Lock para evitar sleep si el navegador lo permite.
 * - Reconexión silenciosa (banner sutil cuando hay error de red).
 * - Colores del layout = branding del tenant (mismo SoT que Customer Display).
 * - Todos los kinds se delegan a `SignageContentRenderer` (compartido con el hub).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { SignageContentRenderer } from "@/components/signage/SignageContentRenderer";
import type { SignageContent } from "@/lib/db-service/signage";
import {
  normalizeSignageBranding,
  signageBrandingCssVars,
  type SignageBranding,
} from "@/lib/signage-branding";

type WakeLockSentinel = { release?: () => Promise<void> } | null;

type PlayerState = {
  ok?: boolean;
  name?: string;
  locationId?: string | null;
  content?: SignageContent;
  updatedAt?: string;
  branding?: SignageBranding;
  error?: string;
};

export const Route = createFileRoute("/signage/$token")({
  component: SignagePlayerPage,
});

function SignagePlayerPage() {
  const { token } = Route.useParams();
  const [state, setState] = useState<PlayerState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const cancelledRef = useRef(false);
  const errorStreakRef = useRef(0);

  useEffect(() => {
    cancelledRef.current = false;
    errorStreakRef.current = 0;

    const load = async () => {
      try {
        const res = await fetch(
          `/api/signage/player?token=${encodeURIComponent(token)}`,
          { credentials: "omit" },
        );
        if (!res.ok) {
          if (cancelledRef.current) return;
          if (res.status === 404) {
            setError("Pantalla no vinculada o revocada");
            setReconnecting(false);
            return;
          }
          errorStreakRef.current += 1;
          setReconnecting(true);
          if (errorStreakRef.current > 4) setError("Error de red");
          return;
        }
        const data = (await res.json()) as PlayerState;
        if (cancelledRef.current) return;
        errorStreakRef.current = 0;
        setError(null);
        setReconnecting(false);
        setState(data);
      } catch {
        if (cancelledRef.current) return;
        errorStreakRef.current += 1;
        setReconnecting(true);
        if (errorStreakRef.current > 4) setError("Sin conexión");
      }
    };

    void load();
    const id = window.setInterval(() => void load(), 5000);
    return () => {
      cancelledRef.current = true;
      window.clearInterval(id);
    };
  }, [token]);

  // SCRUM-136: Registrar Service Worker de offline fallback.
  // Solo en rutas /signage/* — el SW cachea las respuestas del player.
  useEffect(() => {
    if (
      typeof navigator === "undefined" ||
      !("serviceWorker" in navigator) ||
      typeof window === "undefined"
    ) {
      return;
    }
    // Solo registrar si estamos en una ruta /signage/
    if (!window.location.pathname.startsWith("/signage/")) return;

    let registration: ServiceWorkerRegistration | null = null;
    navigator.serviceWorker
      .register("/signage-sw.js", { scope: "/" })
      .then((reg) => {
        registration = reg;
      })
      .catch(() => {
        // SW no disponible (HTTP, modo incógnito, etc.) — silencioso.
      });

    return () => {
      // No se desregistra al desmontar: el SW debe persisitir entre navigaciones
      // para mantener el cache activo. Solo se limpia si el usuario cierra la pestaña.
      void registration;
    };
  }, []); // una sola vez en el mount

  // Wake Lock — mantener la pantalla despierta si el navegador lo permite.
  useEffect(() => {
    let wakeLock: WakeLockSentinel | null = null;
    const request = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Wake Lock API todavía experimental en TS DOM.
        const nav = navigator as any;
        if (nav.wakeLock?.request) {
          wakeLock = (await nav.wakeLock.request("screen")) as WakeLockSentinel;
        }
      } catch {
        wakeLock = null;
      }
    };
    void request();
    return () => {
      wakeLock?.release?.().catch(() => undefined);
    };
  }, []);

  const content = state?.content ?? { kind: "idle" };
  const name = state?.name ?? "Structura Signage";
  const branding = useMemo(
    () => normalizeSignageBranding(state?.branding),
    [state?.branding],
  );
  const shellStyle = useMemo(() => signageBrandingCssVars(branding), [branding]);

  return (
    <div
      className="relative flex h-dvh w-full flex-col overflow-hidden"
      style={shellStyle}
    >
      {error ? (
        <div className="flex flex-1 items-center justify-center p-8 text-center">
          <div>
            <p className="text-3xl font-semibold">{error}</p>
            <p className="mt-4 text-lg opacity-70">Pantalla: {name}</p>
            <p className="mt-2 text-sm opacity-50">Token: {token.slice(0, 8)}…</p>
            <p className="mt-6 text-xs opacity-40">Reintentando cada 5 segundos…</p>
          </div>
        </div>
      ) : (
        <SignageContentRenderer
          content={content}
          name={name}
          branding={branding}
          refreshKey={state?.updatedAt ?? null}
        />
      )}

      {reconnecting && !error ? (
        <div
          className="pointer-events-none absolute right-3 top-3 rounded-full px-3 py-1 text-xs backdrop-blur"
          style={{
            backgroundColor: "color-mix(in srgb, var(--sig-bg) 60%, transparent)",
            color: "var(--sig-muted)",
          }}
        >
          Reconectando…
        </div>
      ) : null}

      <a
        href="/signage/pair?rebind=1"
        className="absolute bottom-2 right-2 z-50 rounded px-2 py-1 text-[10px] opacity-35 hover:opacity-100"
        style={{
          backgroundColor: "color-mix(in srgb, var(--sig-bg) 50%, transparent)",
          color: "var(--sig-fg)",
        }}
        title="Usá esto si cambiaste de comercio y seguís viendo la pantalla anterior"
      >
        Vincular otra pantalla
      </a>
    </div>
  );
}
